// ═══════════════════════════════════════════════════════════════════════════════
// Inventory validation
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { uuidSchema, dateSchema, optionalTimeSchema } from './common.schema.js';
import { INVENTORY_LEVEL } from '../constants/index.js';
import { monthlyKindSchema, recurrenceDaysSchema, recurrenceMonthDaysSchema, recurrenceOrdinalSchema, recurrenceTypeSchema, refineOccurrenceOrStartDates, refineWeekdaySelection, } from './recurrence.schema.js';
export const inventoryItemCreateSchema = z.object({
    name: z.string().min(1).max(200).transform((s) => s.trim()),
    category: z.string().max(100).default('').transform((s) => s.trim()),
});
export const inventoryItemUpdateSchema = z
    .object({
    name: z.string().min(1).max(200).transform((s) => s.trim()).optional(),
    category: z.string().max(100).transform((s) => s.trim()).optional(),
})
    .refine((d) => d.name !== undefined || d.category !== undefined, {
    message: 'At least one of name or category is required',
});
export const inventoryListCreateSchema = z.object({
    name: z.string().min(1).max(200).transform((s) => s.trim()),
});
export const inventoryListUpdateSchema = z.object({
    name: z.string().min(1).max(200).transform((s) => s.trim()),
});
export const inventoryListItemsUpdateSchema = z.object({
    itemIds: z.array(uuidSchema).max(10),
});
const taskDateRangeSchema = z
    .object({
    startDate: dateSchema,
    endDate: dateSchema,
})
    .refine((data) => data.startDate <= data.endDate, {
    message: 'Range end date must be on or after start date',
    path: ['endDate'],
});
/** Stock take task: same recurrence rules as general tasks (`createTaskInputSchema`). */
export const inventoryTaskCreateSchema = z
    .object({
    inventoryListId: uuidSchema.optional(),
    title: z.string().min(1).max(120).transform((s) => s.trim()),
    description: z
        .string()
        .max(1000)
        .default('')
        .transform((s) => s.trim()),
    recurrence: recurrenceTypeSchema.default('none'),
    recurrenceDays: recurrenceDaysSchema,
    monthlyKind: monthlyKindSchema.nullable().default(null),
    recurrenceMonthDays: recurrenceMonthDaysSchema,
    recurrenceOrdinal: recurrenceOrdinalSchema,
    occurrenceDate: z.string().default(''),
    startDate: z.string().default(''),
    endDate: z.string().default(''),
    exclusionRanges: z.array(taskDateRangeSchema).max(100).default([]),
    dueTime: optionalTimeSchema.default(''),
    requirePhoto: z.boolean().default(true),
    notifyEmployer: z.boolean().default(true),
    remindHelperBeforeDue: z.boolean().default(false),
    categories: z.array(z.string().min(1).max(120).transform((s) => s.trim())).max(100).default([]),
    locations: z.array(z.string().min(1).max(150).transform((s) => s.trim())).max(100).default([]),
    notifyExpiryLeadDays: z
        .array(z.number().int().refine((v) => [3, 7, 10, 14, 30].includes(v), 'Invalid lead day'))
        .max(5)
        .default([]),
})
    .refine((data) => data.inventoryListId || (data.categories.length > 0 && data.locations.length > 0), {
    message: 'Either inventoryListId, or categories+locations, is required',
    path: ['inventoryListId'],
})
    .superRefine((data, ctx) => {
    refineWeekdaySelection(data, ctx);
    refineOccurrenceOrStartDates(data, ctx, {
        oneTime: 'One-time stocktake requires occurrence date (YYYY-MM-DD)',
        recurring: 'Recurring stocktake requires start date (YYYY-MM-DD)',
    });
});
const inventoryLineSchema = z.object({
    itemId: uuidSchema,
    status: z.enum([
        INVENTORY_LEVEL.FULL,
        INVENTORY_LEVEL.HALF,
        INVENTORY_LEVEL.LOW,
        INVENTORY_LEVEL.EMPTY,
    ]),
    photoUrl: z.string().min(1),
    aiRaw: z.record(z.string(), z.unknown()).optional().default({}),
    sortOrder: z.number().int().min(0).optional().default(0),
});
const inventorySubmitV1Schema = z.object({
    lines: z.array(inventoryLineSchema).min(1),
    note: z.string().max(2000).default(''),
    completedAt: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/),
});
const inventorySubmitV2ItemSchema = z.object({
    itemName: z.string().min(1).max(200).transform((s) => s.trim()),
    categoryName: z.string().min(1).max(120).transform((s) => s.trim()),
    stockLevel: z.enum([
        INVENTORY_LEVEL.FULL,
        INVENTORY_LEVEL.HALF,
        INVENTORY_LEVEL.LOW,
        INVENTORY_LEVEL.EMPTY,
    ]),
    photoUrl: z.string().default('').transform((s) => s.trim()),
    aiRaw: z.record(z.string(), z.unknown()).optional().default({}),
    expiries: z
        .array(z.object({
        expiryDate: z.string().regex(/^(\d{4}-\d{2}-\d{2}|NO_EXPIRY)$/),
    }))
        .min(1),
    sortOrder: z.number().int().min(0).optional().default(0),
    itemNameEmployerDisplay: z.string().max(200).optional(),
    itemNameHelperSnapshot: z.string().max(200).optional(),
});
const inventoryLocationPhotoEntrySchema = z.object({
    url: z.string().min(1).transform((s) => s.trim()),
    aiAnalysisCompleted: z.boolean(),
});
const inventorySubmitV2LocationSchema = z.object({
    locationName: z.string().min(1).max(150).transform((s) => s.trim()),
    photoUrls: z.array(z.string().min(1).transform((s) => s.trim())).min(1),
    locationPhotos: z.array(inventoryLocationPhotoEntrySchema).min(1).optional(),
    items: z.array(inventorySubmitV2ItemSchema),
    noItemsCategories: z
        .array(z.string().min(1).max(120).transform((s) => s.trim()))
        .default([]),
    sortOrder: z.number().int().min(0).optional().default(0),
});
const inventorySubmitV2Schema = z.object({
    locations: z.array(inventorySubmitV2LocationSchema).min(1),
    note: z.string().max(2000).default(''),
    completedAt: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/),
});
export const inventorySubmitSchema = z.union([inventorySubmitV2Schema, inventorySubmitV1Schema]);
export const inventoryAnalyzeRequestSchema = z.object({
    itemName: z.string().min(1).max(200),
    mimeType: z.string().min(3).max(80),
    imageBase64: z.string().min(1),
});
export const inventoryTaskMetadataSuggestionsResponseSchema = z.object({
    categories: z.array(z.string()),
    locations: z.array(z.string()),
});
//# sourceMappingURL=inventory.schema.js.map